import { useEffect } from 'react'
import { setMetaTags } from '@/utils/seo'
import { Link } from 'react-router-dom'
import { CheckCircle, ChevronRight, Signal } from 'lucide-react'
import SiluxPromise from '@/components/SiluxPromise'

const SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "name": "Rural Broadband UK — Mobile Internet for Rural Areas",
      "provider": { "@type": "Organization", "name": "Silux Mobile", "url": "https://www.siluxmobile.co.uk" },
      "serviceType": "Mobile Broadband",
      "areaServed": "United Kingdom",
      "description": "4G and 5G mobile broadband for rural areas where fibre broadband is unavailable or too slow. Router included and delivered, no landline and no engineer needed. Works anywhere Three, O2 or Vodafone has signal. Plans from 1 month (rolling) to 36 months, from £15.95 a month. No price rises during your contract and free broadband upgrades.",
      "url": "https://www.siluxmobile.co.uk/broadband-rural-uk"
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is the best broadband for rural areas in the UK?", "acceptedAnswer": { "@type": "Answer", "text": "Mobile broadband on Three's 4G/5G network is often the best option for rural areas where fibre broadband is unavailable or too slow. Silux Mobile offers rural broadband from £15.95 a month on a 24-month plan, with the router included, no landline and no engineer visit. Shorter plans are available too — from a 1-month rolling contract (30 days’ notice) to 3, 6, 12 and 18 months." } },
        { "@type": "Question", "name": "Does mobile broadband work in rural UK?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Three's network covers over 99% of the UK population, including most rural villages, farms and remote properties. 4G speeds of 10–50 Mbps on Broadband Lite and 50–150 Mbps on 5G Premium are typical in rural areas — enough for streaming, video calls and working from home." } },
        { "@type": "Question", "name": "How fast is rural mobile broadband?", "acceptedAnswer": { "@type": "Answer", "text": "On Three's 4G network in rural areas, typical download speeds are 10–50 Mbps on Broadband Lite and 50–150 Mbps on 5G Premium. In areas with 5G coverage, speeds are 80–400 Mbps. Both are fast enough for HD streaming, Zoom calls and everyday internet use." } },
        { "@type": "Question", "name": "Is mobile broadband cheaper than fibre in rural areas?", "acceptedAnswer": { "@type": "Answer", "text": "Usually, yes. Rural fibre (where available) often costs £30–50 a month, on top of installation that can run to thousands of pounds for a remote property. Silux Mobile's mobile broadband starts at £15.95 a month on a 24-month plan, with no installation, no engineer and no setup fee — the router simply arrives in the post." } },
        { "@type": "Question", "name": "How long is a rural broadband plan?", "acceptedAnswer": { "@type": "Answer", "text": "You choose, from 1 month: a rolling monthly contract (Broadband Lite from £25.95, 5G Premium from £38.95 a month, 30 days’ notice to leave) or a fixed 3, 6, 12, 18, 24 or 36 months. Broadband Lite is £15.95 a month over 24 months and 5G Premium starts at £19.95 a month over 36 months; 3-month plans start at £19.95 a month plus a £15 refundable holding deposit. Your price is fixed for the life of the plan, and if you move the broadband comes with you." } },
        { "@type": "Question", "name": "What equipment do I need for rural mobile broadband?", "acceptedAnswer": { "@type": "Answer", "text": "A 4G or 5G router (or a portable MiFi) is included in your package and delivered to your door with the SIM. Insert the SIM, connect your devices to the WiFi it creates, and you have broadband — no engineer visit, no landline, nothing to buy separately." } },
        { "@type": "Question", "name": "Will my broadband price increase during my contract?", "acceptedAnswer": { "@type": "Answer", "text": "No. Your price is fixed for the life of your plan — there are no price increases during your contract and no annual inflation-linked price rises. What you sign up at is what you pay." } },
        { "@type": "Question", "name": "Are broadband upgrades free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Silux Mobile offers free broadband upgrades — move to a faster plan or upgrade your router with no upgrade fee. Free upgrades are included as standard." } }
      ]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.siluxmobile.co.uk" },
        { "@type": "ListItem", "position": 2, "name": "Broadband", "item": "https://www.siluxmobile.co.uk/broadband" },
        { "@type": "ListItem", "position": 3, "name": "Rural Broadband UK", "item": "https://www.siluxmobile.co.uk/broadband-rural-uk" }
      ]
    }
  ]
}

const REGIONS = [
  "Scottish Highlands", "Wales", "Devon & Cornwall", "Yorkshire Dales",
  "Cumbria & Lake District", "Lincolnshire Fens", "Norfolk & Suffolk",
  "Shropshire", "Herefordshire", "Northumberland", "Peak District", "Somerset",
]

const SPEEDS = [
  { activity: "Browsing & email", needed: "2 Mbps", mobile4g: "✅ 10–150 Mbps", mobile5g: "✅ 80–400 Mbps" },
  { activity: "HD video streaming", needed: "5 Mbps", mobile4g: "✅ Easily", mobile5g: "✅ Easily" },
  { activity: "4K streaming", needed: "25 Mbps", mobile4g: "✅ Most areas", mobile5g: "✅ Always" },
  { activity: "Video calls (Zoom)", needed: "3 Mbps", mobile4g: "✅ Easily", mobile5g: "✅ Easily" },
  { activity: "Working from home", needed: "10 Mbps", mobile4g: "✅ Yes", mobile5g: "✅ Yes" },
  { activity: "Online gaming", needed: "20 Mbps", mobile4g: "✅ Good ping", mobile5g: "✅ Excellent" },
]

export default function BroadbandRural() {
  useEffect(() => {
    setMetaTags({
      title: 'Rural Broadband UK | 4G & 5G Where Fibre Doesn’t Reach | Silux Mobile',
      description: "Rural broadband for homes and farms fibre cannot reach. 4G & 5G mobile broadband from £15.95/month, router included and delivered — no landline, no engineer, no installation. Plans from 1 month (rolling) to 36 months on Three, O2 and Vodafone.",
      keywords: ['rural broadband UK', 'countryside broadband', 'farm broadband', 'broadband where fibre does not reach', '4G rural internet', 'no landline broadband', 'mobile broadband UK'],
      canonical: 'https://www.siluxmobile.co.uk/broadband-rural-uk',
    })
    const s = document.createElement('script')
    s.type = 'application/ld+json'
    s.text = JSON.stringify(SCHEMA)
    document.head.appendChild(s)
    return () => { document.head.removeChild(s) }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-800 to-green-950 text-white py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <nav className="text-emerald-300 text-sm mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/broadband" className="hover:text-white">Broadband</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Rural Broadband UK</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Rural Broadband UK</h1>
          <p className="text-xl text-emerald-100 mb-3 max-w-2xl">
            Stuck with slow or no fibre in a rural area? Mobile broadband on Three's 4G/5G network works where the cables don't reach — no landline, no engineer, no installation. From £15.95 a month with the router included.
          </p>
          <p className="text-emerald-200 text-base mb-8 max-w-2xl">
            <strong>Mobile broadband for rural areas</strong> uses the same mobile signal your phone relies on, but fed into a 4G/5G router that creates a full home WiFi network. Over 1.5 million UK homes still can't get 'decent broadband' via fibre — for most of them, mobile broadband is the best available solution.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/broadband" className="bg-white text-emerald-800 font-semibold px-6 py-3 rounded-lg hover:bg-emerald-50 transition">
              See Rural Broadband Plans
            </Link>
            <Link to="/network-coverage" className="border border-white text-white px-6 py-3 rounded-lg hover:bg-white/10 transition">
              Check My Coverage
            </Link>
          </div>
        </div>
      </section>

      <SiluxPromise variant="broadband" />

      {/* The rural broadband problem */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">The Rural Broadband Problem</h2>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { stat: "1.5M+", label: "UK homes below 'decent broadband' standard (Ofcom)", color: "text-red-600" },
              { stat: "£3,000+", label: "Average cost to extend fibre to a remote rural property", color: "text-orange-600" },
              { stat: "99%+", label: "UK population covered by Three's mobile network", color: "text-green-600" },
            ].map(s => (
              <div key={s.label} className="text-center bg-gray-50 rounded-xl p-6">
                <p className={`text-4xl font-bold mb-2 ${s.color}`}>{s.stat}</p>
                <p className="text-gray-600 text-sm">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-center max-w-2xl mx-auto">
            For rural properties where fibre isn't coming any time soon, mobile broadband is no longer a stopgap — it's a genuine full-speed solution. Three's rural 4G coverage reaches most villages, farms, and remote homes, and 5G is expanding into rural towns year by year.
          </p>
        </div>
      </section>

      {/* Speed table */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Is Mobile Broadband Fast Enough?</h2>
          <p className="text-gray-500 text-center mb-8">4G covers everything most households need. 5G is a bonus where available.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-xl overflow-hidden shadow-sm">
              <thead className="bg-emerald-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">Activity</th>
                  <th className="px-4 py-3 text-center">Speed Needed</th>
                  <th className="px-4 py-3 text-center">4G Mobile</th>
                  <th className="px-4 py-3 text-center">5G Mobile</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {SPEEDS.map(row => (
                  <tr key={row.activity} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.activity}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{row.needed}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{row.mobile4g}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{row.mobile5g}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Rural Broadband Plans</h2>
          <p className="text-gray-500 text-center mb-10">No landline. No engineer. No setup fee. Plans from 1 month rolling to 36 months.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Broadband Lite", price: "£15.95", data: "Unlimited", tag: "Light users", features: ["Unlimited data on 4G/5G", "24-month plan — best rate", "4G/5G router included", "Free delivery"], popular: false },
              { name: "5G Premium", price: "£19.95", data: "Unlimited 5G", tag: "Most households", features: ["Unlimited data", "36-month plan (12-month £27.95)", "5G router included", "Free delivery"], popular: true },
              { name: "MiFi Portable", price: "£15.95", data: "Take it anywhere", tag: "Barns, fields & away days", features: ["Pocket-sized MiFi included", "24-month plan (12-month £17.50)", "Runs on battery", "Free delivery"], popular: false },
            ].map(p => (
              <div key={p.name} className={`rounded-2xl p-6 border-2 ${p.popular ? 'border-emerald-500 shadow-lg' : 'border-gray-200'} relative`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">BEST FOR RURAL</div>}
                <p className="text-xs text-gray-500 mb-1">{p.tag}</p>
                <h3 className="text-xl font-bold mb-1">{p.name}</h3>
                <p className="text-3xl font-bold text-emerald-700 mb-4">{p.price}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                <ul className="space-y-2 mb-6">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/broadband" className={`block text-center py-2.5 rounded-lg font-semibold text-sm transition ${p.popular ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                  Get This Plan
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            Lite and Premium are also sold on a rolling monthly contract (from £25.95 and £38.95 a month, 30 days’ notice), and every package over 3, 6, 12 and 18 months. 3-month plans start at £19.95/month plus a £15 refundable holding deposit (£32.95 plus a £45 refundable deposit on Premium). No setup fee on any plan.
          </p>
        </div>
      </section>

      {/* Regions */}
      <section className="py-16 px-4 bg-emerald-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-3 text-center">Rural Regions We Cover</h2>
          <p className="text-gray-600 text-center mb-8">Three's network reaches the most remote corners of the UK.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {REGIONS.map(r => (
              <div key={r} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                <Signal className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{r}</span>
              </div>
            ))}
          </div>
          <p className="text-center mt-6">
            <Link to="/network-coverage" className="text-emerald-700 font-semibold hover:underline">Check exact postcode coverage →</Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Rural Broadband FAQs</h2>
          <div className="space-y-4">
            {(SCHEMA["@graph"][1] as any).mainEntity.map((q: any) => (
              <details key={q.name} className="bg-gray-50 rounded-xl border border-gray-200 p-5 group">
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {q.name}
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-gray-600 text-sm mt-3">{q.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-12 px-4 bg-emerald-800 text-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold mb-4 text-center">More from Silux Mobile</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { to: "/broadband-caravan", label: "Caravan Broadband" },
              { to: "/mobile-broadband", label: "Mobile Broadband" },
              { to: "/rolling-monthly-broadband", label: "Rolling Monthly Broadband" },
              { to: "/cheap-broadband", label: "Cheap Broadband" },
              { to: "/data-sim-cards", label: "Data SIM Cards" },
            ].map(l => (
              <Link key={l.to} to={l.to} className="bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 text-center text-sm font-medium transition">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
